/**
 * Copyright IBM Corp. 2022, 2026
 */

import classNames from 'classnames'
import { Button, StandaloneLink } from '@hashicorp/mds-react/components'
import { ActionsProps } from './types'
import { ComponentProps } from 'react'
import s from './style.module.css'

const hasButton = (item) => item.type === 'button' || item.type === undefined
const hasStandaloneLink = (item) => item.type === 'standalone-link'

export default function Actions({
	appearance = 'light',
	layout = 'inline',
	alignment = 'left',
	theme = 'primary',
	size = 'large',
	ctas,
}: ActionsProps) {
	if (ctas.length < 1 || ctas.length > 2) {
		throw new Error(
			`Actions expects at least 1 cta, no more than 2. You provided ${ctas.length}.`,
		)
	}
	let mixed = false
	// If CTAs contain a button and standalone link
	// increase the column gap
	if (ctas.some(hasButton) && ctas.some(hasStandaloneLink)) {
		mixed = true
	}
	return (
		<div
			className={classNames(
				s.actions,
				s[layout],
				s[alignment],
				mixed && s.mixed,
			)}
			data-testid="actions"
		>
			{ctas.map((cta, index) => {
				// The first CTA and second CTA should always
				// have the `primary` and `secondary` variations respectively.

				const standaloneLinkVariant: ComponentProps<
					typeof StandaloneLink
				>['color'] =
					index === 0
						? 'primary'
						: appearance === 'dark'
							? 'secondary-inverted'
							: 'secondary'
				const isStandaloneLink = cta?.type === 'standalone-link'

				if (isStandaloneLink) {
					return (
						<StandaloneLink
							// eslint-disable-next-line react/no-array-index-key
							key={index}
							text={cta.title}
							icon="arrow-right"
							iconPosition="trailing"
							href={cta.href}
							color={standaloneLinkVariant}
							size={size}
							onClick={cta?.onClick}
							data-testid={`standaloneLink-${index}`}
						/>
					)
				} else {
					return (
						<Button
							// eslint-disable-next-line react/no-array-index-key
							key={index}
							text={cta.title}
							href={cta.href}
							onClick={cta.onClick}
							size={size}
							color={theme}
							data-testid={`button-${index}`}
						/>
					)
				}
			})}
		</div>
	)
}
